import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
    apiListProjects,
    apiReportTimesheet,
    apiReportLeaves,
    apiReportRoster,
    apiReportHandovers,
    apiTimesheetOverride,
    apiReportExportUrl,
    formatApiError,
    api as axiosApi,
} from "@/lib/api";
import {
    FileSpreadsheet,
    FileText,
    Download,
    RefreshCw,
    Calendar as CalendarIcon,
    FolderKanban,
} from "lucide-react";

const ALL_PROJECTS = "__all__";

function isoToday() {
    return new Date().toISOString().slice(0, 10);
}
function isoDaysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

async function downloadFile(url, fallbackName) {
    try {
        const res = await axiosApi.get(url.replace(/^.*\/api/, ""), {
            responseType: "blob",
        });
        const blob = new Blob([res.data], {
            type: res.headers["content-type"] || "application/octet-stream",
        });
        const cd = res.headers["content-disposition"] || "";
        const m = /filename="?([^"]+)"?/.exec(cd);
        const filename = m ? m[1] : fallbackName;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (err) {
        toast.error(formatApiError(err));
    }
}

function FiltersBar({ from, to, projectId, onFrom, onTo, onProject, projects, onRefresh, loading }) {
    return (
        <div
            className="bg-white border border-ntt-border rounded-xl p-4 mb-5 flex flex-wrap items-end gap-3"
            data-testid="reports-filters"
        >
            <div>
                <label className="text-[11px] uppercase tracking-wide text-ntt-muted block mb-1">
                    From
                </label>
                <Input
                    type="date"
                    value={from}
                    onChange={(e) => onFrom(e.target.value)}
                    data-testid="filter-from-date"
                    className="h-9 w-[160px]"
                />
            </div>
            <div>
                <label className="text-[11px] uppercase tracking-wide text-ntt-muted block mb-1">
                    To
                </label>
                <Input
                    type="date"
                    value={to}
                    onChange={(e) => onTo(e.target.value)}
                    data-testid="filter-to-date"
                    className="h-9 w-[160px]"
                />
            </div>
            <div>
                <label className="text-[11px] uppercase tracking-wide text-ntt-muted block mb-1">
                    Project
                </label>
                <Select value={projectId || ALL_PROJECTS} onValueChange={onProject}>
                    <SelectTrigger
                        className="h-9 w-[260px]"
                        data-testid="filter-project"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL_PROJECTS} data-testid="filter-project-all">
                            All projects
                        </SelectItem>
                        {projects.map((p) => (
                            <SelectItem
                                key={p.project_id}
                                value={p.project_id}
                                data-testid={`filter-project-${p.project_id}`}
                            >
                                {p.name} · {p.code}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="ml-auto">
                <Button
                    variant="outline"
                    onClick={onRefresh}
                    disabled={loading}
                    data-testid="filter-refresh"
                    className="h-9"
                >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>
        </div>
    );
}

function ExportButtons({ kind, from, to, projectId, extraParams = {} }) {
    const base = { format: "csv", from_date: from, to_date: to };
    if (projectId) base.project_id = projectId;
    const merged = { ...base, ...extraParams };
    const clickable = !!(from && to);
    return (
        <div className="flex items-center gap-2" data-testid={`export-buttons-${kind}`}>
            <Button
                variant="outline"
                disabled={!clickable}
                onClick={() =>
                    downloadFile(
                        apiReportExportUrl(kind, { ...merged, format: "csv" }),
                        `${kind}.csv`,
                    )
                }
                data-testid={`export-${kind}-csv`}
                className="h-9 text-[12px]"
            >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                CSV
            </Button>
            <Button
                variant="outline"
                disabled={!clickable}
                onClick={() =>
                    downloadFile(
                        apiReportExportUrl(kind, { ...merged, format: "xlsx" }),
                        `${kind}.xlsx`,
                    )
                }
                data-testid={`export-${kind}-xlsx`}
                className="h-9 text-[12px]"
            >
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                Excel
            </Button>
            <Button
                disabled={!clickable}
                onClick={() =>
                    downloadFile(
                        apiReportExportUrl(kind, { ...merged, format: "pdf" }),
                        `${kind}.pdf`,
                    )
                }
                data-testid={`export-${kind}-pdf`}
                className="h-9 text-[12px] bg-[#0072BC] hover:bg-[#005a94] text-white"
            >
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                PDF
            </Button>
        </div>
    );
}

// ---------------- Timesheet ----------------
function TimesheetTable({ data, onEdit }) {
    if (!data) return null;
    const rows = data.rows || [];
    const totals = data.totals_by_user || [];
    return (
        <div className="space-y-6">
            <div className="bg-white border border-ntt-border rounded-xl overflow-hidden" data-testid="timesheet-detail">
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#F4F6F9] text-[11px] uppercase tracking-wide text-ntt-muted">
                            <tr>
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-left px-3 py-2">Consultant</th>
                                <th className="text-left px-3 py-2">Project</th>
                                <th className="text-left px-3 py-2">Shift</th>
                                <th className="text-left px-3 py-2">Time</th>
                                <th className="text-right px-3 py-2">Hours (computed)</th>
                                <th className="text-right px-3 py-2">Actual hours</th>
                                <th className="text-right px-3 py-2">On-call (computed)</th>
                                <th className="text-right px-3 py-2">Actual on-call</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={9}
                                        className="text-center text-ntt-muted italic py-10"
                                        data-testid="timesheet-empty"
                                    >
                                        No assignments in this range.
                                    </td>
                                </tr>
                            )}
                            {rows.map((r) => (
                                <tr
                                    key={r.assignment_id}
                                    className="border-t border-ntt-border hover:bg-[#F4F6F9]/60"
                                    data-testid={`timesheet-row-${r.assignment_id}`}
                                >
                                    <td className="px-3 py-2 font-mono text-[12px]">{r.date}</td>
                                    <td className="px-3 py-2">{r.user_full_name}</td>
                                    <td className="px-3 py-2">
                                        <span className="text-ntt-muted">{r.project_code}</span> · {r.project_name}
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.shift_name}
                                        {r.on_call && (
                                            <span className="ml-2 inline-flex h-5 items-center px-1.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium">
                                                On-call
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-[12px] text-ntt-muted">
                                        {r.shift_start} – {r.shift_end}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <span className="text-ntt-muted">{r.computed_hours}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <InlineHoursEdit
                                            value={r.actual_hours}
                                            onChange={(v) => onEdit(r.assignment_id, { actual_hours: v })}
                                            testId={`edit-actual-${r.assignment_id}`}
                                            disabled={r.on_call}
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <span className="text-ntt-muted">{r.computed_on_call_hours}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <InlineHoursEdit
                                            value={r.actual_on_call_hours}
                                            onChange={(v) =>
                                                onEdit(r.assignment_id, { actual_on_call_hours: v })
                                            }
                                            testId={`edit-actual-oncall-${r.assignment_id}`}
                                            disabled={!r.on_call}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div
                className="bg-white border border-ntt-border rounded-xl overflow-hidden"
                data-testid="timesheet-totals"
            >
                <div className="px-4 py-3 border-b border-ntt-border">
                    <h3 className="text-[14px] font-semibold text-[#070F26]">
                        Totals per consultant
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#F4F6F9] text-[11px] uppercase tracking-wide text-ntt-muted">
                            <tr>
                                <th className="text-left px-3 py-2">Consultant</th>
                                <th className="text-right px-3 py-2">Assignments</th>
                                <th className="text-right px-3 py-2">Hours</th>
                                <th className="text-right px-3 py-2">On-call hours</th>
                                <th className="text-right px-3 py-2">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {totals.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center text-ntt-muted italic py-6">
                                        —
                                    </td>
                                </tr>
                            )}
                            {totals.map((t) => (
                                <tr
                                    key={t.user_id}
                                    className="border-t border-ntt-border"
                                    data-testid={`timesheet-total-${t.user_id}`}
                                >
                                    <td className="px-3 py-2 font-medium">{t.user_full_name}</td>
                                    <td className="px-3 py-2 text-right">{t.assignments}</td>
                                    <td className="px-3 py-2 text-right">{t.hours}</td>
                                    <td className="px-3 py-2 text-right">{t.on_call_hours}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-[#0072BC]">
                                        {t.total_hours}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function InlineHoursEdit({ value, onChange, testId, disabled }) {
    const [editing, setEditing] = useState(false);
    const [v, setV] = useState(value ?? "");
    useEffect(() => {
        setV(value ?? "");
    }, [value]);
    if (disabled) {
        return <span className="text-ntt-muted text-[12px]">—</span>;
    }
    if (!editing) {
        return (
            <button
                type="button"
                data-testid={testId}
                onClick={() => setEditing(true)}
                className={`inline-flex items-center h-7 min-w-[64px] justify-end px-2 rounded border ${
                    value != null ? "border-[#0072BC] text-[#0072BC]" : "border-dashed border-ntt-border text-ntt-muted"
                } hover:bg-[#DBEAF6]/40 text-[12px]`}
                title={value != null ? "Click to edit" : "Click to override"}
            >
                {value != null ? value : "override…"}
            </button>
        );
    }
    return (
        <span className="inline-flex items-center gap-1">
            <Input
                type="number"
                step="0.25"
                min="0"
                max="24"
                value={v}
                onChange={(e) => setV(e.target.value)}
                autoFocus
                className="h-7 w-[80px] text-right"
                data-testid={`${testId}-input`}
            />
            <button
                type="button"
                data-testid={`${testId}-save`}
                onClick={() => {
                    const num = v === "" ? null : Number(v);
                    if (num !== null && (isNaN(num) || num < 0 || num > 24)) {
                        toast.error("Hours must be between 0 and 24");
                        return;
                    }
                    onChange(num);
                    setEditing(false);
                }}
                className="text-[11px] text-[#0072BC] hover:underline"
            >
                save
            </button>
            <button
                type="button"
                data-testid={`${testId}-cancel`}
                onClick={() => {
                    setV(value ?? "");
                    setEditing(false);
                }}
                className="text-[11px] text-ntt-muted hover:underline"
            >
                cancel
            </button>
        </span>
    );
}

// ---------------- Leaves ----------------
function LeavesTable({ data }) {
    if (!data) return null;
    const rows = data.rows || [];
    const toneFor = (s) =>
        s === "Approved" ? "green" : s === "Rejected" ? "red" : "amber";
    return (
        <div className="bg-white border border-ntt-border rounded-xl overflow-hidden" data-testid="leaves-table">
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead className="bg-[#F4F6F9] text-[11px] uppercase tracking-wide text-ntt-muted">
                        <tr>
                            <th className="text-left px-3 py-2">Consultant</th>
                            <th className="text-left px-3 py-2">Dates</th>
                            <th className="text-left px-3 py-2">Type</th>
                            <th className="text-left px-3 py-2">Reason</th>
                            <th className="text-left px-3 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-center text-ntt-muted italic py-10">
                                    No leaves in this range.
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr
                                key={r.leave_id}
                                className="border-t border-ntt-border hover:bg-[#F4F6F9]/60"
                                data-testid={`leaves-row-${r.leave_id}`}
                            >
                                <td className="px-3 py-2 font-medium">
                                    {r.user_full_name}
                                    <div className="text-[11px] text-ntt-muted">{r.user_email}</div>
                                </td>
                                <td className="px-3 py-2 font-mono text-[12px]">
                                    {r.start_date} → {r.end_date}
                                </td>
                                <td className="px-3 py-2">{r.type}</td>
                                <td className="px-3 py-2 text-ntt-muted">{r.reason || "—"}</td>
                                <td className="px-3 py-2">
                                    <StatusPill label={r.status} tone={toneFor(r.status)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------- Roster ----------------
function RosterTable({ data }) {
    if (!data) return null;
    const rows = data.rows || [];
    return (
        <div className="bg-white border border-ntt-border rounded-xl overflow-hidden" data-testid="roster-table">
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead className="bg-[#F4F6F9] text-[11px] uppercase tracking-wide text-ntt-muted">
                        <tr>
                            <th className="text-left px-3 py-2">Date</th>
                            <th className="text-left px-3 py-2">Shift</th>
                            <th className="text-left px-3 py-2">Project</th>
                            <th className="text-left px-3 py-2">Consultant</th>
                            <th className="text-left px-3 py-2">On-call</th>
                            <th className="text-left px-3 py-2">Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center text-ntt-muted italic py-10">
                                    No assignments in this range.
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr
                                key={r.assignment_id}
                                className="border-t border-ntt-border hover:bg-[#F4F6F9]/60"
                                data-testid={`roster-row-${r.assignment_id}`}
                            >
                                <td className="px-3 py-2 font-mono text-[12px]">{r.date}</td>
                                <td className="px-3 py-2">
                                    <div>{r.shift_name}</div>
                                    <div className="text-[11px] text-ntt-muted">
                                        {r.shift_start} – {r.shift_end}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <span className="text-ntt-muted">{r.project_code}</span> · {r.project_name}
                                </td>
                                <td className="px-3 py-2">{r.user_full_name}</td>
                                <td className="px-3 py-2">
                                    {r.on_call ? <StatusPill label="On-call" tone="amber" /> : <span className="text-ntt-muted">—</span>}
                                </td>
                                <td className="px-3 py-2 text-ntt-muted">{r.note || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------- Handovers ----------------
function HandoversTable({ data }) {
    if (!data) return null;
    const rows = data.rows || [];
    const toneFor = (s) =>
        s === "Acknowledged" ? "green" : s === "Submitted" ? "blue" : "gray";
    return (
        <div className="bg-white border border-ntt-border rounded-xl overflow-hidden" data-testid="handovers-table">
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead className="bg-[#F4F6F9] text-[11px] uppercase tracking-wide text-ntt-muted">
                        <tr>
                            <th className="text-left px-3 py-2">Date</th>
                            <th className="text-left px-3 py-2">Project</th>
                            <th className="text-left px-3 py-2">Shift</th>
                            <th className="text-left px-3 py-2">Outgoing</th>
                            <th className="text-left px-3 py-2">Incoming</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-right px-3 py-2">Items (total · open · closed)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center text-ntt-muted italic py-10">
                                    No handovers in this range.
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr
                                key={r.handover_id}
                                className="border-t border-ntt-border hover:bg-[#F4F6F9]/60"
                                data-testid={`handovers-row-${r.handover_id}`}
                            >
                                <td className="px-3 py-2 font-mono text-[12px]">{r.handover_date}</td>
                                <td className="px-3 py-2 font-mono text-[12px]">{r.project_code}</td>
                                <td className="px-3 py-2">{r.shift_name}</td>
                                <td className="px-3 py-2">{r.outgoing_user_name || "—"}</td>
                                <td className="px-3 py-2">{r.incoming_user_name || <span className="text-ntt-muted italic">open</span>}</td>
                                <td className="px-3 py-2">
                                    <StatusPill label={r.status} tone={toneFor(r.status)} />
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-[12px]">
                                    {r.item_counts.total} · <span className="text-red-600">{r.item_counts.open}</span> · <span className="text-emerald-700">{r.item_counts.closed}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------- Page ----------------
export default function ReportsPage() {
    const [projects, setProjects] = useState([]);
    const [tab, setTab] = useState("timesheet");
    const [from, setFrom] = useState(isoDaysAgo(6));
    const [to, setTo] = useState(isoToday());
    const [projectId, setProjectId] = useState(ALL_PROJECTS);
    const [loading, setLoading] = useState(false);
    const [timesheet, setTimesheet] = useState(null);
    const [leaves, setLeaves] = useState(null);
    const [roster, setRoster] = useState(null);
    const [handovers, setHandovers] = useState(null);
    const [includeItems, setIncludeItems] = useState(false);

    const effProjectId = projectId === ALL_PROJECTS ? "" : projectId;

    useEffect(() => {
        (async () => {
            try {
                const list = await apiListProjects();
                setProjects(list);
            } catch {
                setProjects([]);
            }
        })();
    }, []);

    const loadCurrent = async () => {
        if (!from || !to) {
            toast.error("Pick both a From and To date");
            return;
        }
        if (to < from) {
            toast.error("To date must be on or after From date");
            return;
        }
        setLoading(true);
        try {
            if (tab === "timesheet") {
                setTimesheet(await apiReportTimesheet({ from_date: from, to_date: to, project_id: effProjectId }));
            } else if (tab === "leaves") {
                setLeaves(await apiReportLeaves({ from_date: from, to_date: to, project_id: effProjectId }));
            } else if (tab === "roster") {
                setRoster(await apiReportRoster({ from_date: from, to_date: to, project_id: effProjectId }));
            } else if (tab === "handovers") {
                setHandovers(
                    await apiReportHandovers({
                        from_date: from,
                        to_date: to,
                        project_id: effProjectId,
                        include_items: includeItems,
                    }),
                );
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCurrent();
        // loadCurrent depends on many form fields; caller controls via tab/includeItems
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, includeItems]);

    async function handleOverride(assignmentId, patch) {
        try {
            await apiTimesheetOverride(assignmentId, patch);
            // reload timesheet
            const data = await apiReportTimesheet({
                from_date: from,
                to_date: to,
                project_id: effProjectId,
            });
            setTimesheet(data);
            toast.success("Timesheet override saved");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    const kindForTab = tab; // timesheet | leaves | roster | handovers

    return (
        <Layout active="reports">
            <PageHeader
                title="Reports & Exports"
                subtitle="Timesheets, leaves, roster and handovers with clean CSV, Excel and PDF exports."
                actions={
                    <ExportButtons
                        kind={kindForTab}
                        from={from}
                        to={to}
                        projectId={effProjectId}
                        extraParams={tab === "handovers" ? { include_items: includeItems } : {}}
                    />
                }
            />
            <FiltersBar
                from={from}
                to={to}
                projectId={projectId === ALL_PROJECTS ? "" : projectId}
                projects={projects}
                onFrom={setFrom}
                onTo={setTo}
                onProject={(v) => setProjectId(v || ALL_PROJECTS)}
                onRefresh={loadCurrent}
                loading={loading}
            />
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="reports-tabs" className="mb-4">
                    <TabsTrigger value="timesheet" data-testid="tab-timesheet">
                        Timesheet
                    </TabsTrigger>
                    <TabsTrigger value="leaves" data-testid="tab-leaves">
                        Leaves
                    </TabsTrigger>
                    <TabsTrigger value="roster" data-testid="tab-roster">
                        Shift roster
                    </TabsTrigger>
                    <TabsTrigger value="handovers" data-testid="tab-handovers">
                        Handovers
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="timesheet">
                    <TimesheetTable data={timesheet} onEdit={handleOverride} />
                </TabsContent>
                <TabsContent value="leaves">
                    <LeavesTable data={leaves} />
                </TabsContent>
                <TabsContent value="roster">
                    <RosterTable data={roster} />
                </TabsContent>
                <TabsContent value="handovers">
                    <div
                        className="flex items-center gap-3 mb-3"
                        data-testid="handovers-include-items-row"
                    >
                        <Switch
                            id="include-items"
                            checked={includeItems}
                            onCheckedChange={setIncludeItems}
                            data-testid="handovers-include-items"
                        />
                        <label
                            htmlFor="include-items"
                            className="text-[13px] text-[#070F26] cursor-pointer"
                        >
                            Include full item detail in PDF / CSV / Excel exports
                        </label>
                    </div>
                    <HandoversTable data={handovers} />
                </TabsContent>
            </Tabs>
        </Layout>
    );
}
